const NUMERALS: Record<string, string> = {
  fa: "\u06f0\u06f1\u06f2\u06f3\u06f4\u06f5\u06f6\u06f7\u06f8\u06f9",
  ar: "\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669",
};

function tagFor(locale: string): string | undefined {
  if (locale === "fa") return "fa";
  if (locale === "ar") return "ar-u-ca-islamic-umalqura-nu-arab";
  return undefined;
}

export function localiseDigits(text: string, locale: string): string {
  const set = NUMERALS[locale];
  if (set === undefined) return text;
  return text.replaceAll(/\d/gu, (digit) => set[Number(digit)] ?? digit);
}

export function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(tagFor(locale)).format(value);
}

const RTF = new Map<string, Intl.RelativeTimeFormat>();

function relativeFormat(locale: string): Intl.RelativeTimeFormat {
  let format = RTF.get(locale);
  if (format === undefined) {
    format = new Intl.RelativeTimeFormat(tagFor(locale), { numeric: "auto" });
    RTF.set(locale, format);
  }
  return format;
}

export function formatDuration(totalSeconds: number, locale: string): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return localiseDigits(hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`, locale);
}

const STAMP: Intl.DateTimeFormatOptions = {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

function withZone(options: Intl.DateTimeFormatOptions, timeZone?: string) {
  return timeZone === undefined ? options : { ...options, timeZone };
}

function readIso(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === "") return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(iso: string, locale: string, timeZone?: string): string {
  const date = readIso(iso);
  if (date === null) return "";
  return new Intl.DateTimeFormat(tagFor(locale), withZone(STAMP, timeZone)).format(date);
}

export function formatDay(iso: string, locale: string): string {
  const date = readIso(iso);
  if (date === null) return "";
  return new Intl.DateTimeFormat(tagFor(locale), {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatClock(iso: string, locale: string): string {
  const date = readIso(iso);
  if (date === null) return "";
  return new Intl.DateTimeFormat(tagFor(locale), {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

export function formatTimeRange(
  startIso: string,
  endIso: string,
  locale: string,
  timeZone?: string,
): string {
  const start = readIso(startIso);
  const end = readIso(endIso);
  const stamp = new Intl.DateTimeFormat(tagFor(locale), withZone(STAMP, timeZone));

  if (start === null && end === null) return "";
  if (start === null) return stamp.format(end as Date);
  if (end === null) return stamp.format(start);
  return stamp.formatRange(start, end);
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatRelative(iso: string, locale: string, now: number = Date.now()): string {
  const date = readIso(iso);

  if (date === null) return "";
  const format = relativeFormat(locale);
  const delta = date.getTime() - now;
  const abs = Math.abs(delta);
  if (abs < MINUTE) return format.format(Math.round(delta / 1000), "second");
  if (abs < HOUR) return format.format(Math.round(delta / MINUTE), "minute");
  if (abs < DAY) return format.format(Math.round(delta / HOUR), "hour");
  return format.format(Math.round(delta / DAY), "day");
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

const UNITS = ["B", "KB", "MB", "GB"] as const;

export function formatBytes(bytes: number, locale: string): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const size = value < 10 && unit > 0 ? value.toFixed(1) : String(Math.round(value));

  return `${localiseDigits(size, locale)} ${UNITS[unit]}`;
}

export function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
